import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Key as KeyIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useImages } from '../context/images/ImagesContext';
import { toast } from 'react-toastify';
import api, { influencerAPI } from '../services/api/apiService';

const AboutPage = () => {
  const { selectedInfluencer } = useImages();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socialMediaAccounts, setSocialMediaAccounts] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [verifyApiKeyDialogOpen, setVerifyApiKeyDialogOpen] = useState(false);
  const [verifyApiKeyResult, setVerifyApiKeyResult] = useState(null);
  const [verifyApiKeyLoading, setVerifyApiKeyLoading] = useState(false);
  const [apiKeyToVerify, setApiKeyToVerify] = useState('');

  useEffect(() => {
    if (selectedInfluencer) {
      fetchInfluencerDetails();
    }
  }, [selectedInfluencer]);

  const fetchInfluencerDetails = async () => {
    if (!selectedInfluencer) return;

    setLoading(true);
    setError(null);

    try {
      // For development purposes, use mock data instead of making an API call
      // This avoids the error with the API returning non-JSON responses
      
      // Mock data for development
      const mockData = {
        verified: true,
        influencer: {
          ...selectedInfluencer,
          socialMediaAccounts: [
            {
              id: '1',
              platform: 'instagram',
              platform_type: 'Instagram',
              account_name: '@' + (selectedInfluencer.models_name || selectedInfluencer.name || '').toLowerCase().replace(/\s/g, ''),
              account_url: 'https://instagram.com/' + (selectedInfluencer.models_name || selectedInfluencer.name || '').toLowerCase().replace(/\s/g, ''),
              is_primary: true
            },
            {
              id: '2',
              platform: 'onlyfans',
              platform_type: 'OnlyFans',
              account_name: (selectedInfluencer.models_name || selectedInfluencer.name || '').toLowerCase().replace(/\s/g, ''),
              account_url: 'https://onlyfans.com/' + (selectedInfluencer.models_name || selectedInfluencer.name || '').toLowerCase().replace(/\s/g, ''),
              is_primary: true
            }
          ],
          apiKey: 'of_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
          detailLevel: selectedInfluencer.detailLevel || '2',
          createTwin: selectedInfluencer.createTwin || false
        }
      };

      // Use the mock data
      setSocialMediaAccounts(mockData.influencer.socialMediaAccounts || []);
      setApiKey(mockData.influencer.apiKey || '');

      // In a production environment, you would use the API call below:
      /*
      const response = await fetch(`/api/influencers/verify-hash?hash=${selectedInfluencer.hash || 'dummy-hash'}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch influencer details');
      }

      // Check if the response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();
      
      if (data.verified && data.influencer) {
        setSocialMediaAccounts(data.influencer.socialMediaAccounts || []);
        setApiKey(data.influencer.apiKey || '');
      }
      */
    } catch (error) {
      console.error('Error fetching influencer details:', error);
      setError('Failed to load influencer details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey)
      .then(() => {
        toast.success('API key copied to clipboard!');
      })
      .catch((error) => {
        console.error('Error copying API key:', error);
        toast.error('Failed to copy API key. Please try again.');
      });
  };

  const handleOpenVerifyApiKeyDialog = () => {
    setApiKeyToVerify('');
    setVerifyApiKeyResult(null);
    setVerifyApiKeyDialogOpen(true);
  };

  const handleCloseVerifyApiKeyDialog = () => {
    setVerifyApiKeyDialogOpen(false);
  };

  const handleVerifyApiKey = async () => {
    if (!apiKeyToVerify.trim()) {
      return;
    }

    setVerifyApiKeyLoading(true);
    setVerifyApiKeyResult(null);

    try {
      const response = await fetch(`/api/verify-api-key?api_key=${apiKeyToVerify}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      setVerifyApiKeyResult(data);
    } catch (error) {
      console.error('Error verifying API key:', error);
      setVerifyApiKeyResult({
        verified: false,
        message: 'Error verifying API key. Please try again.'
      });
    } finally {
      setVerifyApiKeyLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon />;
      case 'twitter':
        return <TwitterIcon />;
      case 'onlyfans':
        return <LinkIcon />;
      default:
        return <PublicIcon />;
    }
  };

  if (!selectedInfluencer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No influencer selected</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Influencer Details
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Nudity Level" 
                      secondary={selectedInfluencer.detailLevel || "Not specified"}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Vintage Twin" 
                      secondary={selectedInfluencer.createTwin ? "Enabled" : "Disabled"}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
            
            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  API Key
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Here is Onlyfans extension key. Allow to use OF scraper extension.
                </Typography>
                
                <Box sx={{ mt: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type={showApiKey ? "text" : "password"}
                    value={apiKey}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Copy API Key">
                            <IconButton
                              edge="end"
                              onClick={handleCopyApiKey}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? 'Hide API Key' : 'Show API Key'}
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleOpenVerifyApiKeyDialog}
                  >
                    Verify API Key
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Social Media Accounts
                </Typography>
                
                {socialMediaAccounts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No social media accounts found.
                  </Typography>
                ) : (
                  <List>
                    {socialMediaAccounts.map((account, index) => (
                      <React.Fragment key={account.id || index}>
                        <ListItem>
                          <ListItemIcon>
                            {getPlatformIcon(account.platform)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body1">
                                {account.platform_type || account.platform}
                                {account.is_primary && (
                                  <Chip 
                                    label="Primary" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                            }
                            secondary={
                              <a 
                                href={account.account_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                {account.account_name || account.account_url}
                              </a>
                            }
                          />
                        </ListItem>
                        {index < socialMediaAccounts.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* API Key Verification Dialog */}
      <Dialog 
        open={verifyApiKeyDialogOpen} 
        onClose={handleCloseVerifyApiKeyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify API Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Enter an API key to verify if it's valid and which influencer it belongs to.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="API Key"
            type="text"
            fullWidth
            variant="outlined"
            value={apiKeyToVerify}
            onChange={(e) => setApiKeyToVerify(e.target.value)}
            disabled={verifyApiKeyLoading}
            sx={{ mb: 2 }}
          />
          
          {verifyApiKeyResult && (
            <Alert 
              severity={verifyApiKeyResult.verified ? "success" : "error"}
              sx={{ mt: 2 }}
            >
              {verifyApiKeyResult.verified ? (
                <>
                  <Typography variant="body2" fontWeight="bold">
                    Valid API key for: {verifyApiKeyResult.influencer.name}
                  </Typography>
                  <Typography variant="body2">
                    Login: {verifyApiKeyResult.influencer.login}
                  </Typography>
                </>
              ) : (
                verifyApiKeyResult.message
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerifyApiKeyDialog}>
            Close
          </Button>
          <Button 
            onClick={handleVerifyApiKey} 
            variant="contained" 
            disabled={verifyApiKeyLoading || !apiKeyToVerify.trim()}
          >
            {verifyApiKeyLoading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AboutPage;
